import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../utils/api";
import styles from "./UserEdit.module.scss";
import "flatpickr/dist/flatpickr.min.css";
import { useAppContext } from "../../context/AppContext";
import CustomInput from "../../components/Inputs/CustomInput";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import CustomCheckbox from "../../components/CustomCheckbox/CustomCheckbox";
import CustomCalendar from "../../components/CustomCalendar/CustomCalendar";
import TabbedFormLayout from "../../components/TabbedFormLayout/TabbedFormLayout";

interface Organization {
  id: string;
  name: string;
}

interface App {
  id: string;
  name: string;
}

const optenicsApp: App = { id: "optenics-platform", name: "Optenics" };

const validLanguageValues = ["en", "es", "fr", "de", "zh", "ja", "ko", "ru", "pt", "ar"];

export default function EditUserPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setEditUserName, setEditOrganizationName } = useAppContext();

  // GENERAL
  const [firstname, setFirstname] = useState("");
  const [middlename, setMiddlename] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [title, setTitle] = useState("");
  const [usertype, setUsertype] = useState("");
  const [issuperadmin, setIsSuperAdmin] = useState(false);

  // AUTH SETTINGS
  const [passwordlessEnabled, setPasswordlessEnabled] = useState(false);
  const [passwordlessMethod, setPasswordlessMethod] = useState("");
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorType, setTwoFactorType] = useState("");
  const [recoveryEmailEnabled, setRecoveryEmailEnabled] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [sessionInvalidated, setSessionInvalidated] = useState(false);
  const [accountLockedUntil, setAccountLockedUntil] = useState("");
  const [passwordLastChanged, setPasswordLastChanged] = useState("");
  const [securityPin, setSecurityPin] = useState("");

  // USER SETTINGS
  const [languagePreference, setLanguagePreference] = useState("");
  const [timezone, setTimezone] = useState("");
  const [companyAddress1, setCompanyAddress1] = useState("");
  const [personalAddress1, setPersonalAddress1] = useState("");
  const [companyAddress2, setCompanyAddress2] = useState("");
  const [companyUnit, setCompanyUnit] = useState("");
  const [companyState, setCompanyState] = useState("");
  const [companyPostal, setCompanyPostal] = useState("");
  const [personalAddress2, setPersonalAddress2] = useState("");
  const [personalUnit, setPersonalUnit] = useState("");
  const [personalState, setPersonalState] = useState("");
  const [personalPostal, setPersonalPostal] = useState("");
  const [shippingAddress1, setShippingAddress1] = useState("");
  const [shippingAddress2, setShippingAddress2] = useState("");
  const [shippingUnit, setShippingUnit] = useState("");
  const [shippingState, setShippingState] = useState("");
  const [shippingPostal, setShippingPostal] = useState("");

  // APP ACCESS
  const [apps, setApps] = useState<string[]>([]);
  const [activeOrganizationId, setActiveOrganizationId] = useState("");
  const [isOptenicsUser, setIsOptenicsUser] = useState(false);
  const [isPortalUser, setIsPortalUser] = useState(false);
  const [isPaidUser, setIsPaidUser] = useState(false);
  const [isAffiliatesUser, setIsAffiliatesUser] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState("{}");

  const [organizationList, setOrganizationList] = useState<Organization[]>([]);
  const [appList, setAppList] = useState<App[]>([]);

  const fetchUser = async () => {
    if (id && id !== "new") {
      try {
        const [coreRes, authRes, settingsRes, orgListRes, appListRes] = await Promise.all([
          api.post("/core/users/get", { userId: id }),
          api.post("/auth/getauth", { userId: id }),
          api.post("/core/settings/get", { userId: id }),
          api.post("/core/organization/user/list", { userId: id }).catch((err) => {
            console.error("[UserEditPage] Failed to fetch user organizations:", err);
            return { data: { organizations: [] } };
          }),
          api.get("/core/app/list"),
        ]);

        console.log("[UserEditPage] Raw core response:", JSON.stringify(coreRes.data, null, 2));
        console.log("[UserEditPage] Raw auth response:", JSON.stringify(authRes.data, null, 2));
        console.log("[UserEditPage] Raw settings response:", JSON.stringify(settingsRes.data, null, 2));
        console.log("[UserEditPage] Raw organization list response:", JSON.stringify(orgListRes.data, null, 2));

        // Handle nested response structure
        const coreData = coreRes.data.data || coreRes.data;
        const authData = authRes.data.data || authRes.data;
        const settingsData = settingsRes.data.data || settingsRes.data;

        const data = {
          ...coreData,
          ...authData,
          ...settingsData,
          issuperadmin: coreData.issuperadmin ?? authData.issuperadmin ?? false
        };

        console.log("[UserEditPage] Merged user data:", JSON.stringify(data, null, 2));

        // GENERAL
        setFirstname(data.firstname || "");
        setMiddlename(data.middlename || "");
        setLastname(data.lastname || "");
        setEmail(data.email || "");
        setPhoneNumber(data.phone_number || "");
        setTitle(data.title || "");
        setUsertype(data.usertype || "");
        setIsSuperAdmin(data.issuperadmin || false);

        // AUTH SETTINGS
        setPasswordlessEnabled(!!data.passwordless_enabled);
        setPasswordlessMethod(data.passwordless_method || "");
        setTwoFactorEnabled(!!data.two_factor_enabled);
        setTwoFactorType(data.two_factor_type || "");
        setRecoveryEmailEnabled(!!data.recovery_email_enabled);
        setRecoveryEmail(data.recovery_email || "");
        setSessionInvalidated(!!data.session_invalidated);
        setAccountLockedUntil(data.account_locked_until || "");
        setPasswordLastChanged(data.password_last_changed || "");
        setSecurityPin(data.security_pin || "");

        // USER SETTINGS
        const languageValue = (data.language_preference || "").toLowerCase();
        setLanguagePreference(
          validLanguageValues.includes(languageValue) ? languageValue : "en"
        );
        setTimezone(data.timezone || "");
        setCompanyAddress1(data.company_address_1 || "");
        setCompanyAddress2(data.company_address_2 || "");
        setCompanyUnit(data.company_unit || "");
        setCompanyState(data.company_state || "");
        setCompanyPostal(data.company_postal || "");
        setPersonalAddress1(data.personal_address_1 || "");
        setPersonalAddress2(data.personal_address_2 || "");
        setPersonalUnit(data.personal_unit || "");
        setPersonalState(data.personal_state || "");
        setPersonalPostal(data.personal_postal || "");
        setShippingAddress1(data.shipping_address_1 || "");
        setShippingAddress2(data.shipping_address_2 || "");
        setShippingUnit(data.shipping_unit || "");
        setShippingState(data.shipping_state || "");
        setShippingPostal(data.shipping_postal || "");

        // APP ACCESS
        setApps(data.apps || []);
        setActiveOrganizationId(data.activeorganizationid || "");
        setIsOptenicsUser(!!data.is_optenics_user);
        setIsPortalUser(!!data.is_portal_user);
        setIsPaidUser(!!data.is_paid_user);
        setIsAffiliatesUser(!!data.is_affiliates_user);
        setNotificationPreferences(
          JSON.stringify(data.notification_preferences || {})
        );

        // ORGANIZATION LIST + APP LIST
        let organizations = orgListRes.data.organizations || [];
        if (organizations.length === 0) {
          console.warn("[UserEditPage] No organizations found, setting fallback from activeorganizationid");
          organizations = [{ id: data.activeorganizationid, name: "Active Organization" }];
        } else {
          // Add active organization if not present
          if (!organizations.some(org => org.id === data.activeorganizationid)) {
            organizations = [...organizations, { id: data.activeorganizationid, name: "Active Organization" }];
          }
        }
        setOrganizationList(organizations);

        let appsFromApi = appListRes.data.apps || [];
        if (!appsFromApi.some((app: App) => app.name === "Optenics")) {
          appsFromApi = [...appsFromApi, optenicsApp];
        }
        setAppList(appsFromApi);

        // Update edit context for header display
        const matchedOrg = organizations.find((org: Organization) => org.id === data.activeorganizationid);
        setEditUserName(`${data.firstname} ${data.lastname}`);
        setEditOrganizationName(matchedOrg?.name || "");

        console.log("[UserEditPage] Fetched user data:", JSON.stringify(data, null, 2));
        console.log("[UserEditPage] Fetched apps:", data.apps);
        console.log("[UserEditPage] App List IDs:", appsFromApi.map((a: App) => a.id));
        console.log("[UserEditPage] User organizations:", organizations);

      } catch (err) {
        console.error("[UserEditPage] Failed to load user:", err);
      }
    }
  };

  useEffect(() => {
    fetchUser();
  }, [id]);

  const cleanString = (value: string) => value && value.trim() !== "" ? value : null;

  const handleSave = async () => {
    try {
      const userData = {
        firstname,
        middlename,
        lastname,
        email,
        phone_number: phoneNumber,
        title,
        usertype,
        issuperadmin,
        passwordless_enabled: passwordlessEnabled,
        passwordless_method: passwordlessEnabled ? cleanString(passwordlessMethod) : null,
        two_factor_enabled: twoFactorEnabled,
        two_factor_type: twoFactorEnabled ? cleanString(twoFactorType) : null,
        recovery_email_enabled: recoveryEmailEnabled,
        recovery_email: recoveryEmail,
        session_invalidated: sessionInvalidated,
        account_locked_until: cleanString(accountLockedUntil),
        password_last_changed: cleanString(passwordLastChanged),
        security_pin: securityPin,
        language_preference: languagePreference,
        timezone,
        company_address_1: companyAddress1,
        company_address_2: companyAddress2,
        company_unit: companyUnit,
        company_state: companyState,
        company_postal: companyPostal,
        personal_address_1: personalAddress1,
        personal_address_2: personalAddress2,
        personal_unit: personalUnit,
        personal_state: personalState,
        personal_postal: personalPostal,
        shipping_address_1: shippingAddress1,
        shipping_address_2: shippingAddress2,
        shipping_unit: shippingUnit,
        shipping_state: shippingState,
        shipping_postal: shippingPostal,
        apps,
        activeorganizationid: activeOrganizationId,
        is_optenics_user: isOptenicsUser,
        is_portal_user: isPortalUser,
        is_paid_user: isPaidUser,
        is_affiliates_user: isAffiliatesUser,
        notification_preferences: JSON.parse(notificationPreferences),
      };

      if (id === "new") {
        await api.post("/core/users/create", userData);
      } else {
        await api.post("/core/users/adminupdateuser", {
          userId: id,
          updates: userData,
        });
      }

      navigate("/users");
    } catch (err) {
      console.error("[UserEditPage] Failed to save user:", err);
    }
  };

  const tabs = [
    {
      label: "General",
      content: (
        <div className={styles.sectionsContainer}>
          <div className={styles.inputSection}>
            <h3 className={styles.sectionTitle}>Personal Details</h3>
            <div className={styles.formGrid}>
              <CustomInput
                label="First Name"
                value={firstname}
                onChange={setFirstname}
                type="text"
              />
              <CustomInput
                label="Last Name"
                value={lastname}
                onChange={setLastname}
                type="text"
              />
              <CustomInput
                label="Middle Name"
                value={middlename}
                onChange={setMiddlename}
                type="text"
              />
              <CustomInput
                label="Email"
                value={email}
                onChange={setEmail}
                type="email"
              />
            </div>
          </div>
          <div className={styles.inputSection}>
            <h3 className={styles.sectionTitle}>Account Details</h3>
            <div className={styles.formGrid}>
              <CustomInput
                label="Phone"
                value={phoneNumber}
                onChange={setPhoneNumber}
                type="tel"
              />
              <CustomInput
                label="Title"
                value={title}
                onChange={setTitle}
                type="text"
              />
              <CustomSelect
                label="User Type"
                value={usertype}
                onChange={setUsertype}
                options={[
                  { value: "admin", label: "Admin" },
                  { value: "manager", label: "Manager" },
                  { value: "user", label: "User" },
                  { value: "superadmin", label: "Super Admin" },
                ]}
                type="normal"
                size="half"
              />
              <CustomCheckbox
                label="Is Super Admin"
                checked={issuperadmin}
                onChange={setIsSuperAdmin}
                id="issuperadmin"
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      label: "Auth Settings",
      content: (
        <div className={styles.sectionsContainer}>
          <div className={styles.inputSection}>
            <h3 className={styles.sectionTitle}>Passwordless Authentication</h3>
            <div className={styles.formGrid}>
              <CustomCheckbox
                label="Passwordless Enabled"
                checked={passwordlessEnabled}
                onChange={setPasswordlessEnabled}
                id="passwordlessEnabled"
              />
              <CustomSelect
                label="Passwordless Method"
                value={passwordlessMethod}
                onChange={setPasswordlessMethod}
                options={[
                  { value: "magic_link", label: "Magic Link" },
                  { value: "otp_email", label: "OTP Email" },
                  { value: "otp_sms", label: "OTP SMS" },
                  { value: "biometric", label: "Biometric" },
                ]}
                type="normal"
                size="half"
              />
            </div>
          </div>
          <div className={styles.inputSection}>
            <h3 className={styles.sectionTitle}>Two-Factor Authentication</h3>
            <div className={styles.formGrid}>
              <CustomCheckbox
                label="Two Factor Enabled"
                checked={twoFactorEnabled}
                onChange={setTwoFactorEnabled}
                id="twoFactorEnabled"
              />
              <CustomSelect
                label="Two Factor Type"
                value={twoFactorType}
                onChange={setTwoFactorType}
                options={[
                  { value: "email", label: "Email" },
                  { value: "sms", label: "SMS" },
                ]}
                type="normal"
                size="half"
              />
            </div>
          </div>
          <div className={styles.inputSection}>
            <h3 className={styles.sectionTitle}>Recovery & Security</h3>
            <div className={styles.formGrid}>
              <CustomCheckbox
                label="Recovery Email Enabled"
                checked={recoveryEmailEnabled}
                onChange={setRecoveryEmailEnabled}
                id="recoveryEmailEnabled"
              />
              <CustomInput
                label="Recovery Email"
                value={recoveryEmail}
                onChange={setRecoveryEmail}
                type="email"
              />
              <CustomCheckbox
                label="Session Invalidated"
                checked={sessionInvalidated}
                onChange={setSessionInvalidated}
                id="sessionInvalidated"
              />
              <CustomInput
                label="Security Pin"
                value={securityPin}
                onChange={setSecurityPin}
                type="password"
              />
              <CustomCalendar
                label="Account Locked Until"
                value={accountLockedUntil}
                onChange={setAccountLockedUntil}
                size="full"
              />
              <CustomCalendar
                label="Password Last Changed"
                value={passwordLastChanged}
                onChange={setPasswordLastChanged}
                size="full"
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      label: "User Settings",
      content: (
        <div className={styles.sectionsContainer}>
          <div className={styles.inputSection}>
            <h3 className={styles.sectionTitle}>Preferences</h3>
            <div className={styles.formGrid}>
              <CustomSelect
                label="Language Preference"
                value={languagePreference}
                onChange={setLanguagePreference}
                type="language"
                required
                size="half"
              />
              <CustomSelect
                label="Timezone"
                value={timezone}
                onChange={setTimezone}
                type="timezone"
                required
                size="half"
              />
            </div>
          </div>
          <div className={styles.inputSection}>
            <h3 className={styles.sectionTitle}>Company Address</h3>
            <div className={styles.formGrid}>
              <CustomInput
                label="Address 1"
                value={companyAddress1}
                onChange={setCompanyAddress1}
                type="text"
              />
              <CustomInput
                label="Address 2"
                value={companyAddress2}
                onChange={setCompanyAddress2}
                type="text"
              />
              <CustomInput
                label="Unit"
                value={companyUnit}
                onChange={setCompanyUnit}
                type="text"
              />
              <CustomSelect
                label="State"
                value={companyState}
                onChange={setCompanyState}
                type="state"
                required
                size="half"
              />
              <CustomInput
                label="Postal"
                value={companyPostal}
                onChange={setCompanyPostal}
                type="text"
              />
            </div>
          </div>
          <div className={styles.inputSection}>
            <h3 className={styles.sectionTitle}>Personal Address</h3>
            <div className={styles.formGrid}>
              <CustomInput
                label="Address 1"
                value={personalAddress1}
                onChange={setPersonalAddress1}
                type="text"
              />
              <CustomInput
                label="Address 2"
                value={personalAddress2}
                onChange={setPersonalAddress2}
                type="text"
              />
              <CustomInput
                label="Unit"
                value={personalUnit}
                onChange={setPersonalUnit}
                type="text"
              />
              <CustomSelect
                label="State"
                value={personalState}
                onChange={setPersonalState}
                type="state"
                required
                size="half"
              />
              <CustomInput
                label="Postal"
                value={personalPostal}
                onChange={setPersonalPostal}
                type="text"
              />
            </div>
          </div>
          <div className={styles.inputSection}>
            <h3 className={styles.sectionTitle}>Shipping Address</h3>
            <div className={styles.formGrid}>
              <CustomInput
                label="Address 1"
                value={shippingAddress1}
                onChange={setShippingAddress1}
                type="text"
              />
              <CustomInput
                label="Address 2"
                value={shippingAddress2}
                onChange={setShippingAddress2}
                type="text"
              />
              <CustomInput
                label="Unit"
                value={shippingUnit}
                onChange={setShippingUnit}
                type="text"
              />
              <CustomSelect
                label="State"
                value={shippingState}
                onChange={setShippingState}
                type="state"
                required
                size="half"
              />
              <CustomInput
                label="Postal"
                value={shippingPostal}
                onChange={setShippingPostal}
                type="text"
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      label: "App Access",
      content: (
        <div className={styles.sectionsContainer}>
          <div className={styles.inputSection}>
            <h3 className={styles.sectionTitle}>Access Controls</h3>
            <div className={styles.formGrid}>
              <CustomSelect
                label="Active Organization"
                value={activeOrganizationId}
                onChange={setActiveOrganizationId}
                options={[
                  { value: "", label: "Select an organization", disabled: true },
                  ...organizationList.map((org) => ({
                    value: org.id,
                    label: org.name,
                  })),
                ]}
                type="normal"
                size="half"
              />
              <CustomInput
                label="Notification Preferences (JSON)"
                value={notificationPreferences}
                onChange={setNotificationPreferences}
                type="text"
              />
              <CustomCheckbox
                label="Is Optenics User"
                checked={isOptenicsUser}
                onChange={setIsOptenicsUser}
                id="isOptenicsUser"
              />
              <CustomCheckbox
                label="Is Portal User"
                checked={isPortalUser}
                onChange={setIsPortalUser}
                id="isPortalUser"
              />
              <CustomCheckbox
                label="Is Paid User"
                checked={isPaidUser}
                onChange={setIsPaidUser}
                id="isPaidUser"
              />
              <CustomCheckbox
                label="Is Affiliates User"
                checked={isAffiliatesUser}
                onChange={setIsAffiliatesUser}
                id="isAffiliatesUser"
              />
            </div>
          </div>
          <div className={styles.inputSection}>
            <h3 className={styles.sectionTitle}>App Access</h3>
            <div className={styles.formGrid}>
              {appList.map((app) => (
                <CustomCheckbox
                  key={app.id}
                  label={app.name}
                  checked={apps.includes(app.id)}
                  onChange={(checked) => {
                    setApps((prev) =>
                      checked ? [...prev, app.id] : prev.filter((a) => a !== app.id)
                    );
                  }}
                  id={`app-${app.id}`}
                />
              ))}
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <TabbedFormLayout
      tabs={tabs}
      onSave={handleSave}
      onCancel={() => navigate("/users")}
      showActions={true}
    />
  );
}
